/**
 * Game Configuration & Balancing Data for "Bertahan Dari Anomali"
 * All balance parameters, skill cooldowns, and entity stats are defined here.
 */

export const CANVAS_WIDTH = 640;
export const CANVAS_HEIGHT = 360;

export const MAP_WIDTH = 1600;
export const MAP_HEIGHT = 1200;

export interface SkillConfig {
  name: string;
  cooldown: number; // in seconds
  damageMultiplier: number;
  energyCost: number;
  description: string;
}

export const PLAYER_CONFIG = {
  maxHp: 420,
  maxEnergy: 120,
  baseAttack: 34,
  baseDefense: 14,
  moveSpeed: 175,
  runSpeed: 240,
  hitRadius: 18,
  hurtInvulnerabilityMs: 380,
  
  combo: [
    {
      name: "Tebasan 1",
      startupMs: 70,
      activeMs: 65,
      recoveryMs: 75,
      damageMultiplier: 1.0,
      range: 50,
      angleDeg: 75,
      knockback: 18,
      hitstopMs: 50,
    },
    {
      name: "Tebasan 2",
      startupMs: 60,
      activeMs: 65,
      recoveryMs: 80,
      damageMultiplier: 1.2,
      range: 54,
      angleDeg: 85,
      knockback: 24,
      hitstopMs: 60,
    },
    {
      name: "Tebasan 3 Finisher",
      startupMs: 80,
      activeMs: 80,
      recoveryMs: 120,
      damageMultiplier: 1.6,
      range: 65,
      angleDeg: 115,
      knockback: 55,
      hitstopMs: 90,
    },
  ],
  comboResetWindowMs: 950,

  flashStep: {
    name: "Flash Step",
    cooldown: 1.8,
    distance: 185,
    durationMs: 130,
    invulnerabilityMs: 240,
    energyCost: 12,
    ghostCount: 3,
    ghostOpacities: [0.45, 0.25, 0.10],
  },

  crescentSlash: {
    name: "Crescent Slash",
    cooldown: 2.8,
    speed: 520,
    range: 380,
    damageMultiplier: 1.65,
    energyCost: 20,
    width: 44,
    height: 18,
    knockback: 32,
  },

  spinStrike: {
    name: "Spin Strike",
    cooldown: 3.8,
    radius: 100,
    durationMs: 220,
    damageMultiplier: 1.5,
    energyCost: 25,
    knockback: 42,
    hitstopMs: 75,
  },

  specialAttack: {
    name: "Anomaly Break",
    cooldown: 8.5,
    chargeMs: 220,
    strikeRange: 140,
    damageMultiplier: 3.4,
    energyCost: 45,
    hitstopMs: 180,
    screenShake: 14,
    knockback: 85,
  },

  potion: {
    maxCharges: 3,
    healAmount: 160,
    cooldown: 4.5,
  },

  targetLock: {
    maxDistance: 420,
    breakDistance: 520,
  },
};

export type EnemyType = "melee" | "heavy" | "ranged" | "fast";

export interface EnemyStats {
  type: EnemyType;
  name: string;
  maxHp: number;
  attack: number;
  defense: number;
  speed: number;
  detectRadius: number;
  attackRadius: number;
  preferredDistance?: number;
  telegraphShape: "cone" | "rect" | "circle" | "line";
  telegraphDurationMs: number;
  recoveryDurationMs: number;
  telegraphSize: {
    radius?: number;
    angleDeg?: number;
    width?: number;
    length?: number;
  };
  knockbackPower: number;
  scoreValue: number;
}

export const ENEMY_CONFIGS: Record<EnemyType, EnemyStats> = {
  melee: {
    type: "melee",
    name: "Melee Anomaly",
    maxHp: 130,
    attack: 24,
    defense: 8,
    speed: 86,
    detectRadius: 280,
    attackRadius: 58,
    telegraphShape: "cone",
    telegraphDurationMs: 520,
    recoveryDurationMs: 340,
    telegraphSize: { radius: 66, angleDeg: 75 },
    knockbackPower: 25,
    scoreValue: 150,
  },
  heavy: {
    type: "heavy",
    name: "Heavy Anomaly",
    maxHp: 380,
    attack: 46,
    defense: 22,
    speed: 48,
    detectRadius: 260,
    attackRadius: 80,
    telegraphShape: "rect",
    telegraphDurationMs: 720,
    recoveryDurationMs: 500,
    telegraphSize: { width: 58, length: 120 },
    knockbackPower: 65,
    scoreValue: 400,
  },
  ranged: {
    type: "ranged",
    name: "Ranged Anomaly",
    maxHp: 85,
    attack: 26,
    defense: 6,
    speed: 72,
    detectRadius: 360,
    attackRadius: 270,
    preferredDistance: 180,
    telegraphShape: "cone",
    telegraphDurationMs: 580,
    recoveryDurationMs: 420,
    telegraphSize: { radius: 240, angleDeg: 36 },
    knockbackPower: 18,
    scoreValue: 200,
  },
  fast: {
    type: "fast",
    name: "Spear Anomaly",
    maxHp: 105,
    attack: 22,
    defense: 7,
    speed: 115,
    detectRadius: 300,
    attackRadius: 75,
    telegraphShape: "circle",
    telegraphDurationMs: 420,
    recoveryDurationMs: 260,
    telegraphSize: { radius: 58 },
    knockbackPower: 30,
    scoreValue: 220,
  },
};
